import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import {
  getTasks,
  getUsers,
  assign_tasks,
  assigned_tasks,
  unassign_tasks,
  highlight_task,
  publish_plan,
  get_all_clients,
  get_all_Projects,
  create_new_task,
  un_highlight_task,
  plan_analytics,
  get_plans,
  get_speechmatic_jwt,
  updateLeave
} from './api';

const initialState = {
  loading: false,
  user: null,
  planAnalyticsLoading: false,
  tasksList: []
};

const actions = {
  GET_TASKS: 'tasks/GET_TASKS',
  GET_USERS: 'tasks/GET_USERS',
  ASSIGN_TASK_TO_USER: 'tasks/ASSIGN_TASK_TO_USER',
  GET_ASSIGNED_TASKS: 'tasks/GET_ASSIGNED_TASKS',
  UNASSIGNED_TASK: 'tasks/UNASSIGNED_TASKS',
  HIGHLIGHT_TASK: 'tasks/HIGHLIGHT_TASK',
  UN_HIGHLIGHT_TASK: 'tasks/UN_HIGHLIGHT_TASK',
  PUBLISH_PLAN: 'tasks/PUBLISH_PLAN',
  PLAN_ANALYTICS: 'tasks/PLAN_ANALYTICS',
  GET_CLIENTS: 'tasks/GET_CLIENTS',
  GET_PROJECTS: 'tasks/GET_PROJECTS',
  CREATE_NEW_TASK: 'tasks/CREATE_NEW_TASK',
  PLANS: 'tasks/PLANS',
  PLANS_SUMMARY: 'tasks/PLANS_SUMMARY',
  GET_SPEECHMATIC_JWT: 'tasks/GET_SPEECHMATICS_JWT',
  UPDATE_USER_LEAVE: 'tasks/UPDATE_USER_LEAVE'
};

export const getAllTasks = createAsyncThunk(
  actions.GET_TASKS,
  async (payload) => {
    const response = await getTasks('all_tasks', payload);

    return response;
  }
);

export const getAllUsers = createAsyncThunk(
  actions.GET_USERS,
  async (payload) => {
    const response = await getUsers('all_users', payload);
    return response;
  }
);

export const updateTaskAssignment = createAsyncThunk(
  actions.ASSIGN_TASK_TO_USER,
  async (payload) => {
    const response = await assign_tasks('assign_task', payload);
    return response;
  }
);

export const getAssignedTasks = createAsyncThunk(
  actions.GET_ASSIGNED_TASKS,
  async (payload) => {
    const response = await assigned_tasks('assigned_tasks', payload);
    return response;
  }
);

export const unAssignedTask = createAsyncThunk(
  actions.UNASSIGNED_TASK,
  async (payload) => {
    const response = await unassign_tasks('unassign_task', payload);
    return response;
  }
);

export const highlightTask = createAsyncThunk(
  actions.HIGHLIGHT_TASK,
  async (payload) => {
    const response = await highlight_task('highlight_task', payload);
    return response;
  }
);

export const unhighlightTask = createAsyncThunk(
  actions.UN_HIGHLIGHT_TASK,
  async (payload) => {
    const response = await un_highlight_task('unhighlight_task', payload);
    return response;
  }
);

export const publishPlan = createAsyncThunk(
  actions.PUBLISH_PLAN,
  async (payload) => {
    const response = await publish_plan('publish_task', payload);
    return response;
  }
);

export const getPlanAnalytics = createAsyncThunk(
  actions.PLAN_ANALYTICS,
  async (payload) => {
    const response = await plan_analytics('get-plan-analytics', payload);
    return response;
  }
);

export const getAllClients = createAsyncThunk(
  actions.GET_CLIENTS,
  async (payload) => {
    const response = await get_all_clients('client_list', payload);
    return response;
  }
);

export const getAllProjects = createAsyncThunk(
  actions.GET_PROJECTS,
  async (payload) => {
    const response = await get_all_Projects('project_list', payload);
    return response;
  }
);

export const createNewTask = createAsyncThunk(
  actions.CREATE_NEW_TASK,
  async (payload) => {
    const response = await create_new_task('create_task', payload);
    return response;
  }
);

export const getSpeechmaticJWT = createAsyncThunk(
  actions.GET_SPEECHMATIC_JWT,
  async (payload) => {
    const response = await get_speechmatic_jwt(
      'get-speechmatic-token',
      payload
    );
    return response;
  }
);

export const getPlans = createAsyncThunk(actions.PLANS, async (payload) => {
  const response = await get_plans('plan-summary', payload);
  return response;
});

export const viewPlans = createAsyncThunk(
  actions.PLANS_SUMMARY,
  async (payload) => {
    const response = await get_plans('view-plans', payload);
    return response;
  }
);

export const updateUserLeaveStatus = createAsyncThunk(
  actions.UPDATE_USER_LEAVE,
  async (payload) => {
    const response = await updateLeave('update-user-leave-status', payload);
    return response;
  }
);

export const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTasks.fulfilled, (state, action) => {
        const { success, message: msg, data } = action.payload;
        state.loading = false;
        if (success) {
          state.tasksList = data;
        } else {
          message.error(msg);
        }
      })
      .addCase(getAllTasks.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(updateTaskAssignment.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskAssignment.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(updateTaskAssignment.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(getAssignedTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAssignedTasks.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(getAssignedTasks.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(unAssignedTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(unAssignedTask.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(unAssignedTask.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(highlightTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(highlightTask.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(highlightTask.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(unhighlightTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(unhighlightTask.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(unhighlightTask.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(publishPlan.pending, (state) => {
        state.loading = true;
      })
      .addCase(publishPlan.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(publishPlan.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(getPlanAnalytics.pending, (state) => {
        state.planAnalyticsLoading = true;
      })
      .addCase(getPlanAnalytics.fulfilled, (state, action) => {
        state.planAnalyticsLoading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(getPlanAnalytics.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.planAnalyticsLoading = false;

        message.error(msg);
      });

    builder
      .addCase(getAllClients.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllClients.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(getAllClients.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(getAllProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllProjects.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(getAllProjects.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(createNewTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNewTask.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(createNewTask.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(getSpeechmaticJWT.pending, (state) => {
        // Don't set global loading state for speech token requests
        // This prevents UI flickering when getting the token
      })
      .addCase(getSpeechmaticJWT.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        // Only show error if there's an actual message and it failed
        if (!success && msg && msg.trim() !== '') {
          message.error(msg);
        }
      })
      .addCase(getSpeechmaticJWT.rejected, (state, action) => {
        const { message: msg } = action.error;
        // Only show error if there's an actual message
        if (msg && msg.trim() !== '') {
          message.error(msg);
        }
      });

    builder
      .addCase(getPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPlans.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(getPlans.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(viewPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(viewPlans.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(viewPlans.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });
    builder
      .addCase(updateUserLeaveStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserLeaveStatus.fulfilled, (state, action) => {
        state.loading = false;

        const { success, message: msg } = action.payload;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(updateUserLeaveStatus.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message);
      });
  }
});

export const selectTaskSlice = (state) => {
  return state.tasks;
};

export default tasksSlice.reducer;
